import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config/api';

const MessageBox = styled.section`
  min-height: 100vh;
  padding: 120px 2rem 4rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const LoadingTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 1.25rem;
  text-align: left;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const AccessCard = styled.div`
  max-width: 560px;
  padding: 2rem;
  background: var(--glass-bg);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 16px;
  color: var(--text-secondary, #ccc);
  line-height: 1.6;
  border: 1px solid var(--glass-border);
  box-shadow: var(--shadow-lg);

  h2 {
    color: var(--text-primary, #fff);
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;
  }

  a {
    color: var(--accent-color, #7c3aed);
  }
`;

const AdminAnalyticsRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user?.token) {
      setAccess(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/analytics/access`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (cancelled) return;

        if (response.status === 401) {
          setAccess('unauthorized');
          return;
        }
        if (response.ok) {
          setAccess(true);
          return;
        }
        setAccess(false);
      } catch {
        if (!cancelled) setAccess(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  if (loading || access === null) {
    return (
      <MessageBox>
        <LoadingTitle>Loading Analytics…</LoadingTitle>
      </MessageBox>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (access === 'unauthorized') {
    return <Navigate to="/login" replace />;
  }

  if (!access) {
    return (
      <MessageBox>
        <AccessCard>
          <h2>Access denied</h2>
          <p>
            This area is restricted. If you believe this is a mistake, contact the site owner.
          </p>
          <p>
            <a href="/dashboard">Back to dashboard</a>
          </p>
        </AccessCard>
      </MessageBox>
    );
  }

  return children;
};

export default AdminAnalyticsRoute;
